import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs/lib/anime.es.js";
import { Settings } from "../../../settings";
import {
  FEATURES_CONFIG,
  WIN_TYPES,
  WIN_TYPE_THRESHOLDS,
} from "../../../featuresConfig";
import { PixiSettings } from "../../../pixiSettings";
import { ANIMATION_TIMINGS } from "../../../textureConfigs";
import {
  setWinAnimationsPlaying,
  setSymbolPopupBlocked,
} from "../../../State/actions";
import { getWinType, formatToCustomLocale } from "../../../utilities";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";
import { HowlCache } from "../../../State/Slices/soundsSlice";

/**
 * Get animation progression values for counter animations
 * Uses win thresholds as transition points for smooth progression
 */
const getAnimationProgressionValues = () => ({
  // Use win thresholds as the progression steps
  BIG_WIN_PROGRESSION: FEATURES_CONFIG.WIN_THRESHOLDS.MEGA_WIN_THRESHOLD, // 40x
  MEGA_WIN_PROGRESSION: FEATURES_CONFIG.WIN_THRESHOLDS.SUPER_MEGA_WIN_THRESHOLD, // 60x
  SUPER_MEGA_WIN_PROGRESSION:
    FEATURES_CONFIG.WIN_THRESHOLDS.SUPER_MEGA_WIN_THRESHOLD, // 60x
});

/**
 * WinAnimationController - Manages progressive win celebration animations
 *
 * Features:
 * - Progressive win sequence: BIG_WIN → SUPER_WIN → MEGA_WIN → EPIC_WIN
 * - Each win follows: IN animation → IDLE loop → OUT animation
 * - Integrated counter attached to spine's "value" bone (auto-synced position/scale/rotation)
 * - Counter value animates with sound effects
 * - User can skip counter animation or close entire sequence
 * - Auto-close after counter finishes (configurable delay)
 *
 * @class
 */
class WinAnimationController {
  /**
   * Creates a new WinAnimationController instance
   *
   * @param {PIXI.Container} container - Parent container for spine animations
   * @param {Object} dimensionsContext - Device dimensions and context
   * @param {Object} overlayRef - Reference to background overlay
   * @param {Function} dispatch - Redux dispatch function
   * @param {number} symbolWidth - Width of game symbols for scaling
   * @param {number} symbolHeight - Height of game symbols for scaling
   * @param {Object} clickIndicatorRef - Reference to click indicator UI
   * @param {Object} buttonRef - Reference to continue button UI
   * @param {Function} t - Translation function from i18next
   */
  constructor(
    container,
    dimensionsContext,
    overlayRef,
    dispatch,
    symbolWidth,
    symbolHeight,
    clickIndicatorRef,
    buttonRef,
    t
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.overlayRef = overlayRef;
    this.dispatch = dispatch;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.clickIndicatorRef = clickIndicatorRef;
    this.buttonRef = buttonRef;
    this.t = t;

    // Extract dimensions object for backward compatibility
    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.winSpine = null;
    this.isVisible = false;
    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.initialized = false;

    // Counter attached to spine bone
    this.counterContainer = null;
    this.counterBitmapTexts = [];
    this.currentCounterValue = 0;
    this.counterAnimationRef = null;

    // Button attached to spine bone
    this.buttonContainer = null;
    this.buttonText = null;

    // Timeouts and cleanup
    this.currentTimeouts = [];
    this.emergencyRecoveryTimeout = null;
    this.autoCloseTimeout = null;
    this.skipProtectionTimeout = null;
    this.debounceTimeout = null;

    // Animation state
    this.animationQueue = [];
    this.currentAnimationIndex = 0;
    this.finalWinType = null;
    this.lastWinAmount = null; // Prevent restart for same win

    // Activity and phase tracking
    this.isAppActive = true;
    this.pausedTimeouts = [];
    this.lastPauseTime = 0;
    this.totalPausedDuration = 0;
    this.currentAnimationPhase = null;
    this.phaseStartTime = 0;
    this.phaseProgress = 0;

    // User interaction state
    this.hasHandledSkipClick = false;
    this.hasHandledCloseClick = false;
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;
    this.isClosing = false;
    this.isFinishing = false;
    this.counterJumpedToFinal = false;

    // Background music state tracking
    this.backgroundMusicWasPlaying = false;
    this.backgroundMusicType = null; // Track which background music was playing (background_music or free_spin_background_music)

    // Initialize click indicator to hidden state
    this.hideClickIndicatorInstant();

    this.initializeWinSpine();
  }

  /**
   * Apply proportional scaling and centering to spine animation
   * Scales based on symbol size and device type, positions relative to screen center
   */
  applyScaleAndCenter() {
    if (!this.winSpine || !this.dimensions) return;

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT; // Use height as reference since symbolHeight = symbolHeight
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Apply device/orientation-specific multipliers (similar to button scaling)
    const deviceMultiplier = this.getDeviceScaleMultiplier();
    scale *= deviceMultiplier;

    this.winSpine.scale.set(scale);
    // Position relative to centered parent container (0,0 is now screen center)
    this.winSpine.x = 0;
    this.winSpine.y = -(this.symbolHeight / 2 || 0);
  }

  /**
   * Get device-specific scale multiplier for spine animations
   */
  getDeviceScaleMultiplier() {
    const { device, orientation } = this.dimensionsContext;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        // Mobile portrait: smaller size to fit better
        return 1.0;
      } else {
        // Mobile landscape: slightly smaller
        return 0.9;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        // Tablet portrait: slightly smaller
        return 1.0;
      } else {
        // Tablet landscape: normal size
        return 1.0;
      }
    } else {
      // Desktop: full size
      return 0.85;
    }
  }

  /**
   * Show click indicator with fade-in animation
   */
  showClickIndicator() {
    if (this.clickIndicatorRef && this.clickIndicatorRef.current) {
      anime({
        targets: this.clickIndicatorRef.current,
        alpha: 1,
        duration: 300,
        easing: "easeOutQuad",
      });
    }
  }

  /**
   * Hide click indicator with fade-out animation
   */
  hideClickIndicator() {
    if (this.clickIndicatorRef && this.clickIndicatorRef.current) {
      anime({
        targets: this.clickIndicatorRef.current,
        alpha: 0,
        duration: 300,
        easing: "easeOutQuad",
      });
    }
  }

  /**
   * Hide click indicator instantly (for cleanup)
   */
  hideClickIndicatorInstant() {
    if (this.clickIndicatorRef && this.clickIndicatorRef.current) {
      this.clickIndicatorRef.current.alpha = 0.001;
    }
  }

  /**
   * Show continue button with fade-in animation
   */
  showButton() {
    // Show new PIXI button attached to bone
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 1,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    // Keep old React button hidden (for backward compatibility)
    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Hide continue button with fade-out animation
   */
  hideButton() {
    // Hide new PIXI button attached to bone
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 0,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    // Keep old React button hidden (for backward compatibility)
    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Update button text based on current device (tap vs click)
   */
  updateButtonText() {
    if (!this.buttonText) return;

    // Get translated button text based on current device
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    // Update text content
    this.buttonText.text = buttonText;

    // Recalculate font size (always 70, matching initialization)
    this.buttonText.style.fontSize = 70;
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   */
  updateDimensions(dimensionsContext, symbolWidth, symbolHeight) {
    // Check if any values actually changed to avoid unnecessary recalculations
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightsChanged =
      this.symbolWidth !== symbolWidth || this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    // Only proceed if something actually changed - this prevents unnecessary updates during animation start
    if (!dimensionsChanged && !symbolHeightsChanged) {
      return;
    }

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions; // Update the extracted dimensions object
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    // Update button text if device changed (tap vs click)
    if (deviceChanged && this.buttonText) {
      this.updateButtonText();
    }

    // CRITICAL: Always update spine positioning whenever dimensions change, even when hidden
    // This ensures the spine is always positioned correctly, preventing jumps on first appearance
    // Similar to FreeSpinIntroController which maintains positioning at all times
    if (
      this.winSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightsChanged)
    ) {
      // Ensure we have valid symbolHeight before positioning
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }

    // Update counter display if dimensions changed (font size needs adjustment based on spine scale)
    if (dimensionsChanged || symbolHeightsChanged) {
      if (this.counterContainer && this.currentCounterValue !== undefined) {
        // Font size is now based on spine scale, so update when spine is rescaled
        this.updateCounterDisplay(this.currentCounterValue);
      }
    }
  }

  /**
   * Clear all tracked timeouts and timers
   *
   * Clears animation timeouts, auto-close timers, skip protection timers,
   * and emergency recovery timers. Prevents memory leaks and ensures
   * clean state when animations are interrupted.
   */
  clearAllTimeouts() {
    this.currentTimeouts.forEach(clearTimeout);
    this.currentTimeouts = [];

    // Clear skip protection timeout
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }

    // Clear emergency recovery timeout
    if (this.emergencyRecoveryTimeout) {
      clearTimeout(this.emergencyRecoveryTimeout);
      this.emergencyRecoveryTimeout = null;
    }

    // Clear auto-close timeout
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
      this.autoCloseTimeout = null;
    }

    // Clear debounce timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }
  }

  /**
   * Clear all resources (just timeouts now, anime fades handle themselves)
   */
  clearAllResources() {
    this.clearAllTimeouts();
  }

  /**
   * Handle app activity changes (tab focus/blur, app backgrounding)
   *
   * Pauses all animations and timeouts when app becomes inactive,
   * resumes them when app becomes active again. Critical for
   * preventing animation drift and maintaining proper timing.
   *
   * @param {boolean} isActive - Whether app is currently active/focused
   */
  handleActivityChange(isActive) {
    if (this.isAppActive === isActive) {
      return; // No change
    }

    this.isAppActive = isActive;

    if (!isActive) {
      this.pauseAnimations();
    } else {
      this.resumeAnimations();
    }
  }

  /**
   * Pause spine animations and timeouts when app becomes inactive
   */
  pauseAnimations() {
    // Store pause time for progress calculation
    this.lastPauseTime = performance.now();

    // Calculate progress within current phase if we have timing info
    if (this.phaseStartTime > 0) {
      const elapsed = this.lastPauseTime - this.phaseStartTime;
      this.phaseProgress = Math.min(1, elapsed / this.getPhaseDuration());
    }

    // Pause spine animation by setting timeScale to 0
    if (this.winSpine && this.winSpine.state) {
      this.winSpine.state.timeScale = 0;
    }

    // Clear all resources during pause - we'll reconstruct them on resume
    this.clearAllResources();
  }

  /**
   * Resume spine animations and timeouts when app becomes active
   */
  resumeAnimations() {
    // Validate spine state before resuming
    if (!this.winSpine || !this.winSpine.state) {
      if (this.isPlaying) {
        // Try to reinitialize spine if we're supposed to be playing
        this.initializeWinSpine();
        if (!this.winSpine || !this.winSpine.state) {
          this.finishAnimation();
          return;
        }
      }
    }

    // Resume spine animation by restoring timeScale
    if (this.winSpine && this.winSpine.state) {
      this.winSpine.state.timeScale = 1;
    }

    // Calculate total paused duration and adjust phase start time
    const pauseDuration = performance.now() - this.lastPauseTime;
    this.totalPausedDuration += pauseDuration;
    this.phaseStartTime += pauseDuration; // Adjust phase timing

    // Validate animation state before resuming
    if (this.isPlaying && this.currentAnimationPhase === null) {
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
      return;
    }

    // Resume the correct animation flow based on current state
    this.resumeCurrentPhase();
  }

  /**
   * Get the expected duration for the current animation phase
   */
  getPhaseDuration() {
    switch (this.currentAnimationPhase) {
      case "spine-animation":
        if (
          this.currentAnimationIndex >= 0 &&
          this.currentAnimationIndex < this.animationQueue.length
        ) {
          return ANIMATION_TIMINGS.SPINE_ANIMATION_DURATION;
        }
        return ANIMATION_TIMINGS.SPINE_ANIMATION_DURATION; // Default fallback
      case "waiting-for-click":
        return Infinity; // No timeout for this phase
      default:
        return ANIMATION_TIMINGS.SPINE_ANIMATION_DURATION; // Default fallback
    }
  }

  /**
   * Resume the current animation phase with proper timing
   */
  resumeCurrentPhase() {
    if (!this.isPlaying) {
      return;
    }

    const phaseDuration = this.getPhaseDuration();
    const elapsed = this.phaseProgress * phaseDuration;
    const remaining = Math.max(0, phaseDuration - elapsed);

    switch (this.currentAnimationPhase) {
      case "spine-animation":
        this.resumeSpineAnimationPhase(remaining);
        break;
      case "waiting-for-click":
        // Already waiting, no timeout needed
        break;
      default:
        // Unknown phase, try to continue naturally
        break;
    }
  }

  /**
   * Resume spine animation phase with remaining time
   */
  resumeSpineAnimationPhase(remainingTime) {
    if (remainingTime <= 0) {
      // Current animation should have completed, move to next
      this.currentAnimationIndex++;
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
      this.playNextAnimation();
    } else {
      // Current animation still has time remaining
      // The spine animation itself will continue naturally due to timeScale restoration
      // We just need to set up the timeout for the next phase
      const timeout = setTimeout(() => {
        if (this.isPlaying && this.isAppActive) {
          this.currentAnimationIndex++;
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
          this.playNextAnimation();
        }
      }, remainingTime);
      this.currentTimeouts.push(timeout);
    }
  }

  /**
   * Initialize the win celebration spine
   */
  initializeWinSpine() {
    try {
      this.winSpine = this.createWinSpine();

      if (!this.winSpine) {
        return;
      }

      // Simple validation
      if (!this.winSpine.skeleton || !this.winSpine.state) {
        return;
      }

      // Add to container first
      this.container.addChild(this.winSpine);

      // Set initial properties
      this.winSpine.alpha = 0.001; // Hidden initially (use alpha not visible)

      // Apply initial position and scale (only if dimensions are ready)
      // If dimensions aren't ready yet, updateDimensions will position it when they become available
      if (this.symbolHeight && this.symbolHeight > 0 && this.dimensions) {
        this.applyScaleAndCenter();
      }

      this.initialized = true;

      // Initialize spine structure to compile shaders during loading
      this.initializeSpineStructure();

      // Initialize counter attached to spine bone
      this.initializeCounter();

      // Initialize button attached to spine bone
      this.initializeButton();
    } catch (error) {
      // Silent fail - spine initialization failed
    }
  }

  /**
   * Create win spine from loaded assets
   */
  createWinSpine() {
    try {
      // Check if assets are loaded
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "win_celebration-skeleton";
      const atlasAlias = "win_celebration-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const winSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!winSpine || !winSpine.skeleton || !winSpine.state) {
        return null;
      }

      if (!winSpine.skeleton.data || !winSpine.skeleton.data.animations) {
        return null;
      }

      return winSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   * Minimal requirement: set one animation + update once
   */
  initializeSpineStructure() {
    if (!this.winSpine || !this.winSpine.state) {
      return;
    }

    try {
      // Set any animation to force spine to load bones/slots/attachments
      // Just pick the first available animation (usually big_win_in)
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.winSpine.state.setAnimation(0, animations[0], false);
        this.winSpine.state.update(0.016); // Update one frame
        this.winSpine.skeleton.updateWorldTransform(); // Populate hierarchy
      }

      // Keep at alpha = 0.001 so PixiJS continues rendering it
      // This keeps GPU buffers hot after skeleton is populated
      this.winSpine.alpha = 0.001;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Get available animations from the spine
   */
  getAvailableAnimations() {
    if (!this.winSpine?.skeleton?.data?.animations) return [];
    return this.winSpine.skeleton.data.animations.map((anim) => anim.name);
  }

  /**
   * Initialize counter and attach it to spine's "value" bone
   *
   * The counter is a child of the spine and follows the "value" bone's transform.
   * Position, rotation, and scale are controlled by the bone animation.
   * The counter only manages its own text content and visibility.
   */
  initializeCounter() {
    if (!this.winSpine || !this.winSpine.skeleton) {
      return;
    }

    // Create counter container as child of spine
    this.counterContainer = new PIXI.Container();
    this.counterContainer.name = "win_counter_attached_to_bone";
    this.counterContainer.alpha = 0.001;
    this.counterContainer.zIndex = 1000; // Render above spine effects

    this.winSpine.addChild(this.counterContainer);
    this.winSpine.sortableChildren = true;

    // Initialize with zero
    this.updateCounterDisplay(0);
  }

  /**
   * Update counter display with new value
   *
   * Font size auto-scales to fit the spine's natural counter area (600x200).
   * Long numbers automatically scale down to fit within 90% of available width.
   * Since counter is attached to spine bone, all position/scale/rotation comes from spine.
   *
   * @param {number} value - The value to display
   */
  updateCounterDisplay(value) {
    if (!this.counterContainer) return;

    // Clear existing text
    this.counterBitmapTexts.forEach((text) => text.destroy());
    this.counterBitmapTexts = [];
    this.counterContainer.removeChildren();

    const formattedText = formatToCustomLocale(Math.floor(value));

    // Counter area dimensions in spine's natural size (before bone scaling)
    const availableWidth = 600;
    const availableHeight = 200;

    // Start with ideal font size (45% of height - increased for better visibility)
    const idealFontSize = Math.round(availableHeight * 0.45);

    // Estimate text width with ideal size
    const idealDigitSpacing = idealFontSize * 0.8;
    const idealSeparatorSpacing = idealFontSize * 0.5;
    let estimatedWidth = 0;
    for (let i = 0; i < formattedText.length; i++) {
      const char = formattedText[i];
      estimatedWidth +=
        char === "," || char === "."
          ? idealSeparatorSpacing
          : idealDigitSpacing;
    }

    // Scale down if text exceeds 90% of available width
    let fontSize = idealFontSize;
    if (estimatedWidth > availableWidth * 0.9) {
      fontSize = Math.round(
        (idealFontSize * (availableWidth * 0.9)) / estimatedWidth
      );
    }

    // Calculate final character spacing
    const digitSpacing = fontSize * 0.8;
    const separatorSpacing = fontSize * 0.5;

    // Calculate total width for centering
    let totalWidth = 0;
    for (let i = 0; i < formattedText.length; i++) {
      const char = formattedText[i];
      totalWidth +=
        char === "," || char === "." ? separatorSpacing : digitSpacing;
    }

    let currentX = -totalWidth / 2 + digitSpacing / 2;

    // Create BitmapText for each character
    for (let i = 0; i < formattedText.length; i++) {
      const char = formattedText[i];
      let adjustedX = currentX;

      // Center separators within their allocated space
      if (char === "," || char === ".") {
        const spacingDifference = digitSpacing - separatorSpacing;
        adjustedX -= spacingDifference / 2;
      }

      const bitmapText = new PIXI.BitmapText(char, {
        fontName: "font",
        fontSize: fontSize,
      });

      bitmapText.anchor.set(0.5);
      bitmapText.x = adjustedX;
      bitmapText.y = 0;

      this.counterContainer.addChild(bitmapText);
      this.counterBitmapTexts.push(bitmapText);

      currentX +=
        char === "," || char === "." ? separatorSpacing : digitSpacing;
    }
  }

  /**
   * Sync counter transform with spine's "text_value_spacer" bone
   * Called every frame while spine is animating
   * Applies device-specific Y offset for responsive positioning
   */
  updateCounterFromBone() {
    if (!this.winSpine || !this.counterContainer || !this.winSpine.skeleton) {
      return;
    }

    const valueBone = this.winSpine.skeleton.findBone("text_value_spacer");
    if (!valueBone) {
      return;
    }

    // Get device-specific Y offset
    const yOffset = this.getCounterYOffset();

    // Apply bone's world transform to counter with device-specific Y offset
    this.counterContainer.x = valueBone.worldX;
    this.counterContainer.y = valueBone.worldY + yOffset;
    this.counterContainer.rotation =
      (valueBone.getWorldRotationX() * Math.PI) / 180;
    this.counterContainer.scale.set(
      valueBone.getWorldScaleX(),
      valueBone.getWorldScaleY()
    );
  }

  /**
   * Show counter (visibility only, spine controls position/scale/rotation)
   */
  showCounter() {
    if (!this.counterContainer) return;
    this.counterContainer.alpha = 1;
  }

  /**
   * Hide counter (visibility only, spine controls position/scale/rotation)
   */
  hideCounter() {
    if (!this.counterContainer) return;
    this.counterContainer.alpha = 0.001;
  }

  /**
   * Initialize button and attach it to spine's "text_tap_to_contiune" bone
   *
   * The button is a child of the spine and follows the bone's transform.
   * Position, rotation, and scale are controlled by the bone animation.
   */
  initializeButton() {
    if (!this.winSpine || !this.winSpine.skeleton) {
      return;
    }

    // Create button container as child of spine
    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "continue_button_attached_to_bone";
    this.buttonContainer.alpha = 0.001;
    this.buttonContainer.zIndex = 1001; // Render above counter

    // Get translated button text based on device
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    // Create text style (similar to TextButton but simpler)
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: 70, // Will be scaled by bone
      fontWeight: "700",
      fill: "#ffffff",
      dropShadow: true,
      dropShadowColor: "#000000",
      dropShadowDistance: 4,
      dropShadowBlur: 4,
      dropShadowAlpha: 0.8,
      align: "center",
    });

    // Create PIXI.Text
    this.buttonText = new PIXI.Text(buttonText, textStyle);
    this.buttonText.anchor.set(0.5);
    this.buttonText.x = 0;
    this.buttonText.y = 0;

    // Scale down the button (0.7x smaller)
    this.buttonText.scale.set(0.7);

    this.buttonContainer.addChild(this.buttonText);
    this.winSpine.addChild(this.buttonContainer);
    this.winSpine.sortableChildren = true;
  }

  /**
   * Get device-specific Y offset for counter positioning
   * Adjusts counter position based on device and orientation
   */
  getCounterYOffset() {
    const { device, orientation } = this.dimensionsContext;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return 50; // Mobile portrait offset
      } else {
        return 50; // Mobile landscape offset
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return 50; // Tablet portrait offset
      } else {
        return 50; // Tablet landscape offset
      }
    } else {
      return 50; // Desktop offset
    }
  }

  /**
   * Get device-specific Y offset for button positioning
   * Adjusts button position based on device and orientation
   */
  getButtonYOffset() {
    const { device, orientation } = this.dimensionsContext;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return 445; // Mobile portrait offset
      } else {
        return 250; // Mobile landscape offset
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return 425; // Tablet portrait offset
      } else {
        return 300; // Tablet landscape offset
      }
    } else {
      return 250; // Desktop offset
    }
  }

  /**
   * Sync button transform with spine's "text_tap_to_contiune" bone
   * Called every frame while spine is animating
   * Applies device-specific Y offset for responsive positioning
   */
  updateButtonFromBone() {
    if (!this.winSpine || !this.buttonContainer || !this.winSpine.skeleton) {
      return;
    }

    const buttonBone = this.winSpine.skeleton.findBone("text_tap_to_contiune");
    if (!buttonBone) {
      return;
    }

    // Get device-specific Y offset
    const yOffset = this.getButtonYOffset();

    // Apply bone's world transform to button with device-specific Y offset
    this.buttonContainer.x = buttonBone.worldX;
    this.buttonContainer.y = buttonBone.worldY + yOffset;
    this.buttonContainer.rotation =
      (buttonBone.getWorldRotationX() * Math.PI) / 180;
    this.buttonContainer.scale.set(
      buttonBone.getWorldScaleX(),
      buttonBone.getWorldScaleY()
    );
  }

  /**
   * Animate counter value from one number to another
   *
   * @param {number} fromValue - Starting value
   * @param {number} toValue - Target value
   * @param {number} duration - Animation duration in ms
   * @param {Function} onComplete - Callback when animation completes
   */
  startCounterAnimation(fromValue, toValue, duration, onComplete) {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = fromValue;
    this.updateCounterDisplay(fromValue);

    // Start counter sound
    soundManager.play(SOUND_NAME.counter);

    this.counterAnimationRef = anime({
      targets: this,
      currentCounterValue: toValue,
      duration: duration,
      easing: "easeOutQuad",
      update: () => {
        this.updateCounterDisplay(this.currentCounterValue);
      },
      complete: () => {
        this.currentCounterValue = toValue;
        this.updateCounterDisplay(toValue);
        this.counterAnimationRef = null;

        // Stop counter sound
        soundManager.stop(SOUND_NAME.counter);

        // Call completion callback
        if (onComplete && typeof onComplete === "function") {
          onComplete();
        }
      },
    });
  }

  /**
   * Instantly set counter to specific value (skips animation)
   *
   * @param {number} value - Value to display
   */
  jumpCounterToValue(value) {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = value;
    this.updateCounterDisplay(value);
    soundManager.stop(SOUND_NAME.counter);
  }

  /**
   * Reset counter to zero
   */
  resetCounter() {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = 0;
    this.updateCounterDisplay(0);
    soundManager.stop(SOUND_NAME.counter);
  }

  /**
   * Check if counter value is currently animating
   *
   * @returns {boolean} True if counter is animating
   */
  isCounterAnimating() {
    return this.counterAnimationRef !== null;
  }

  /**
   * Get current counter value
   *
   * @returns {number} Current counter value
   */
  getCurrentCounterValue() {
    return this.currentCounterValue || 0;
  }

  /**
   * Calculate which win animations should play based on win multiplier
   *
   * Determines progressive win sequence based on win amount relative to bet:
   * - 20x+ bet = BIG_WIN animation
   * - 40x+ bet = BIG_WIN + MEGA_WIN animations
   * - 60x+ bet = BIG_WIN + MEGA_WIN + SUPER_MEGA_WIN animations
   *
   * @param {number} winAmount - Total win amount in credits
   * @param {number} betAmount - Bet amount for multiplier calculation
   * @returns {string[]} Array of win types in sequence (e.g., ['big_win', 'mega_win'])
   */
  calculateAnimationSequence(winAmount, betAmount) {
    if (!winAmount || !betAmount || winAmount <= 0 || betAmount <= 0) {
      return [];
    }

    const finalWinType = getWinType(winAmount, betAmount);

    // If it's not an animated win type, return empty array
    if (!finalWinType) {
      return [];
    }

    // Build sequence from lowest to final win type - ordered: BIG, MEGA, SUPER_MEGA
    const winTypeOrder = [
      WIN_TYPES.BIG_WIN,
      WIN_TYPES.MEGA_WIN,
      WIN_TYPES.SUPER_MEGA_WIN,
    ];

    const sequence = [];
    const multiplier = winAmount / betAmount;

    // Add all win types that the multiplier qualifies for
    for (const winType of winTypeOrder) {
      if (this.shouldPlayWinType(winType, multiplier)) {
        sequence.push(winType);
      }

      // Stop when we reach the final win type
      if (winType === finalWinType) {
        break;
      }
    }

    return sequence;
  }

  /**
   * Check if a win type should be played based on multiplier
   */
  shouldPlayWinType(winType, multiplier) {
    const threshold = WIN_TYPE_THRESHOLDS[winType] || 0;
    return threshold > 0 && multiplier >= threshold;
  }

  /**
   * Get counter animation range for a win type
   * Returns {start: number, end: number} for counter animation
   * Uses win thresholds as progression points for consistent animation
   */
  getCounterRange(winType, betAmount, totalWinAmount, animationIndex) {
    if (!betAmount) return { start: 0, end: 0 };

    const progression = getAnimationProgressionValues();

    // First animation always starts from 0
    if (animationIndex === 0) {
      switch (winType) {
        case WIN_TYPES.BIG_WIN:
          return { start: 0, end: betAmount * progression.BIG_WIN_PROGRESSION };
        case WIN_TYPES.MEGA_WIN:
          return {
            start: 0,
            end: betAmount * progression.MEGA_WIN_PROGRESSION,
          };
        case WIN_TYPES.SUPER_MEGA_WIN:
          return {
            start: 0,
            end: betAmount * progression.SUPER_MEGA_WIN_PROGRESSION,
          };
        default:
          return { start: 0, end: 0 };
      }
    }

    // Subsequent animations continue from previous threshold, but last one goes to actual win
    const isLastAnimation = animationIndex === this.animationQueue.length - 1;

    switch (winType) {
      case WIN_TYPES.MEGA_WIN:
        return isLastAnimation
          ? {
              start: betAmount * progression.BIG_WIN_PROGRESSION,
              end: totalWinAmount,
            }
          : {
              start: betAmount * progression.BIG_WIN_PROGRESSION,
              end: betAmount * progression.MEGA_WIN_PROGRESSION,
            };
      case WIN_TYPES.SUPER_MEGA_WIN:
        return {
          start: betAmount * progression.MEGA_WIN_PROGRESSION,
          end: totalWinAmount,
        };
      default:
        return { start: 0, end: 0 };
    }
  }

  /**
   * Play banner sound for win type (plays for each animation)
   * Uses the win sound directly (no banner versions exist)
   */
  playBannerSound = (winType) => {
    // Use the win sound directly (no banner versions)
    if (!this.dispatch || !SOUND_NAME[winType]) return;

    try {
      soundManager.play(SOUND_NAME[winType]);
    } catch (error) {
      // Silent fail
    }
  };

  /**
   * Fade in win music on loop for entire sequence (replaces individual win sounds)
   */
  playMainWinSound = () => {
    try {
      // Fade in win_music on loop for the entire sequence
      const winMusic = HowlCache[SOUND_NAME.win_music];
      if (winMusic) {
        winMusic.loop(true); // Ensure it loops
        soundManager.fadeIn(SOUND_NAME.win_music, 500);
      }
    } catch (error) {
      // Silent fail
    }
  };

  /**
   * Fade out background music if it's playing (smooth transition to win music)
   */
  pauseBackgroundMusic() {
    try {
      // Check both background music options and fade out whichever is playing
      const backgroundMusic = HowlCache[SOUND_NAME.background_music];
      const freeSpinBackgroundMusic =
        HowlCache[SOUND_NAME.free_spin_background_music];

      if (backgroundMusic && backgroundMusic.playing()) {
        this.backgroundMusicWasPlaying = true;
        this.backgroundMusicType = SOUND_NAME.background_music;
        soundManager.fadeOut(SOUND_NAME.background_music, 500);
      } else if (freeSpinBackgroundMusic && freeSpinBackgroundMusic.playing()) {
        this.backgroundMusicWasPlaying = true;
        this.backgroundMusicType = SOUND_NAME.free_spin_background_music;
        soundManager.fadeOut(SOUND_NAME.free_spin_background_music, 500);
      } else {
        this.backgroundMusicWasPlaying = false;
        this.backgroundMusicType = null;
      }
    } catch (error) {
      // Silent fail
      this.backgroundMusicWasPlaying = false;
      this.backgroundMusicType = null;
    }
  }

  /**
   * Fade in background music if it was playing before (smooth transition from win music)
   */
  resumeBackgroundMusic() {
    try {
      if (this.backgroundMusicWasPlaying && this.backgroundMusicType) {
        soundManager.fadeIn(this.backgroundMusicType, 500);
      }
      this.backgroundMusicWasPlaying = false; // Reset flag
      this.backgroundMusicType = null;
    } catch (error) {
      // Silent fail
      this.backgroundMusicWasPlaying = false;
      this.backgroundMusicType = null;
    }
  }

  /**
   * Stop banner sounds (win sounds specific to each animation)
   * Uses the win sound directly (no banner versions exist)
   */
  stopBannerSounds() {
    if (!this.dispatch) return;

    try {
      // Stop win sounds used for animations (no banner versions exist)
      const bannerSounds = [
        SOUND_NAME.big_win,
        SOUND_NAME.mega_win,
        SOUND_NAME.super_mega_win,
      ];

      bannerSounds.forEach((soundName) => {
        if (soundName) {
          soundManager.stop(soundName);
        }
      });
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Stop all win sounds (banner + main)
   */
  stopAllWinSounds() {
    this.stopBannerSounds();
    this.stopWinSounds();
    // Also stop win_music_end if playing (for emergency cleanup)
    try {
      if (SOUND_NAME.win_music_end) {
        soundManager.stop(SOUND_NAME.win_music_end);
      }
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Fade out main win music (replaces stopping individual win sounds)
   */
  stopWinSounds() {
    if (!this.dispatch) return;

    try {
      // Fade out win_music instead of stopping it abruptly
      if (SOUND_NAME.win_music) {
        soundManager.fadeOut(SOUND_NAME.win_music, 500);
      }
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Start the progressive win animation sequence
   *
   * Determines which win animations to play based on win amount and bet size,
   * then plays them in sequence (BIG → SUPER → MEGA → EPIC).
   * Each animation follows: IN → IDLE → OUT pattern.
   * Counter animation runs in sync with spine animations.
   *
   * @param {number} winAmount - Total win amount in credits
   * @param {number} betAmount - Bet amount for win type threshold calculation
   * @returns {Promise} Resolves when animation sequence is complete
   */
  startSequence(winAmount, betAmount) {
    return new Promise((resolve, reject) => {
      // Prevent retriggering during finishing phase and duplicate wins
      if (
        this.isPlaying ||
        this.isFinishing ||
        winAmount === this.lastWinAmount ||
        !this.winSpine ||
        !this.winSpine.state
      ) {
        resolve();
        return;
      }

      // Calculate which animations to play
      this.animationQueue = this.calculateAnimationSequence(
        winAmount,
        betAmount
      );

      if (this.animationQueue.length === 0) {
        // No animations to play
        resolve();
        return;
      }

      this.isPlaying = true;
      this.currentAnimationIndex = 0; // Start at 0 for first animation
      this.finalWinType = this.animationQueue[this.animationQueue.length - 1];
      this.totalWinAmount = winAmount;
      this.betAmount = betAmount;
      this.lastWinAmount = winAmount; // Track this win amount to prevent restart
      this.counterJumpedToFinal = false; // Reset counter jump flag

      // Reset user interaction guards at the beginning of a sequence
      this.hasHandledSkipClick = false;
      this.hasHandledCloseClick = false;
      this.isHandlingUserClick = false;
      this.isWaitingForClick = false; // Reset waiting state for new sequence
      this.isClosing = false; // Reset closing state for new sequence

      // SKIP PROTECTION: Block skipping for the first second
      this.canSkipAnimation = false;
      if (this.skipProtectionTimeout) {
        clearTimeout(this.skipProtectionTimeout);
      }
      this.skipProtectionTimeout = setTimeout(() => {
        // Safety check: only enable skip if animation is still playing and not finished
        if (
          this.isPlaying &&
          !(this.hasHandledSkipClick && this.hasHandledCloseClick)
        ) {
          this.canSkipAnimation = true;
          this.showClickIndicator();
          this.showButton();
        }
      }, 1000); // 1 second protection

      // Enable scaling calculations when animation becomes active
      this.shouldBeActive = true;

      // Pause background music before starting win animation
      this.pauseBackgroundMusic();

      // Spine is already positioned correctly at all times (via updateDimensions)
      // Just ensure final positioning check before showing (spine is always warmed up and positioned)
      if (
        this.winSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      // Show and start animation - positioning is already correct since spine is always maintained
      this.showAndStartAnimation(resolve, reject);
    });
  }

  /**
   * Show spine, counter, overlay and start animation (extracted for reuse)
   */
  showAndStartAnimation(resolve, reject) {
    // Fade in spine smoothly (synchronized with counter/overlay)
    if (this.winSpine) {
      anime({
        targets: this.winSpine,
        alpha: [0.001, 1],
        duration: 350, // Match counter fade-in duration
        easing: "easeOutQuad",
      });
    }

    this.isVisible = true;
    this.currentAnimationPhase = "spine-animation";
    this.phaseStartTime = performance.now();
    this.phaseProgress = 0;

    // Initialize counter
    this.resetCounter();
    this.showCounter();
    this.startBoneUpdateLoop();

    // Show background overlay (synchronized)
    this.overlayRef.current?.fadeInBackground();

    // Start animation after positioning is confirmed
    this.startFirstAnimation().then(resolve).catch(reject);
  }

  /**
   * Start syncing counter and button with spine bone every frame
   * Uses PIXI.Ticker to update counter and button transforms from bones
   */
  startBoneUpdateLoop() {
    if (!this.winSpine || !this.counterContainer) return;

    if (!this.boneUpdateTicker) {
      this.boneUpdateTicker = () => {
        if (this.isPlaying && this.isVisible) {
          this.updateCounterFromBone();
          this.updateButtonFromBone();
        }
      };
      this.container.on("added", this.boneUpdateTicker);
      PIXI.Ticker.shared.add(this.boneUpdateTicker);
    }
  }

  /**
   * Stop syncing counter with spine bone
   */
  stopBoneUpdateLoop() {
    if (this.boneUpdateTicker) {
      PIXI.Ticker.shared.remove(this.boneUpdateTicker);
      this.container.off("added", this.boneUpdateTicker);
      this.boneUpdateTicker = null;
    }
  }

  /**
   * Play the next animation in the queue
   */
  playNextAnimation() {
    return new Promise((resolve, reject) => {
      if (this.currentAnimationIndex >= this.animationQueue.length) {
        resolve();
        return;
      }

      const winType = this.animationQueue[this.currentAnimationIndex];
      const isLastAnimation =
        this.currentAnimationIndex === this.animationQueue.length - 1;

      // Play banner sound for this animation
      this.playBannerSound(winType);

      // Setup spine animation
      this.winSpine.state.clearTracks();
      this.winSpine.state.clearListeners();
      this.winSpine.state.apply(this.winSpine.skeleton);
      this.winSpine.skeleton.setToSetupPose();

      const animations = this.getAvailableAnimations();
      const { inAnimation, idleAnimation, outAnimation } = this.findAnimations(
        animations,
        winType
      );

      if (isLastAnimation) {
        // Last animation: play in → idle → wait for user click
        this.playFinalAnimation(inAnimation, idleAnimation, resolve, reject);
      } else {
        // Intermediate animation: play in → idle → out → next
        this.playIntermediateAnimation(
          inAnimation,
          idleAnimation,
          outAnimation,
          resolve,
          reject
        );
      }
    });
  }

  /**
   * Start first animation in the sequence
   */
  startFirstAnimation() {
    return new Promise((resolve, reject) => {
      if (
        !this.winSpine ||
        !this.winSpine.state ||
        this.animationQueue.length === 0
      ) {
        resolve();
        return;
      }

      const winType = this.animationQueue[0]; // Start with first animation
      const isLastAnimation = this.animationQueue.length === 1;

      // Play main win sound once at animation start (based on final win type)
      this.playMainWinSound();

      // Play banner sound for first animation
      this.playBannerSound(winType);

      // Setup spine animation
      this.winSpine.state.clearTracks();
      this.winSpine.state.clearListeners();
      this.winSpine.state.apply(this.winSpine.skeleton);
      this.winSpine.skeleton.setToSetupPose();

      // Get animation names
      const animations = this.getAvailableAnimations();
      const { inAnimation, idleAnimation } = this.findAnimations(
        animations,
        winType
      );

      if (isLastAnimation) {
        // For single animation, play proper in->idle sequence
        if (inAnimation && idleAnimation) {
          // Play in animation for its actual duration
          this.winSpine.state.setAnimation(0, inAnimation, false);

          // Get actual duration for in animation from spine data
          const inDuration = this.getSpineAnimationDuration(`${winType}_in`);

          // Start counter animation
          if (this.totalWinAmount > 0) {
            this.startCounterAnimation(
              0,
              this.totalWinAmount,
              ANIMATION_TIMINGS.SPINE_ANIMATION_DURATION,
              () => {
                this.startAutoCloseTimer();
              }
            );
          }

          // After in animation completes, start looping idle animation
          const inTimeout = setTimeout(() => {
            if (this.isPlaying && this.isAppActive) {
              // Add looping idle animation and wait for user click
              this.winSpine.state.addAnimation(0, idleAnimation, true, 0);
              this.isWaitingForClick = true;
              this.currentAnimationPhase = "waiting-for-click";
              this.phaseStartTime = performance.now();
              this.phaseProgress = 0;
              resolve();
            }
          }, inDuration);

          this.currentTimeouts.push(inTimeout);
        } else if (idleAnimation) {
          // Fallback: just loop idle animation if in animation not available
          this.winSpine.state.setAnimation(0, idleAnimation, true);
          this.isWaitingForClick = true;
          this.currentAnimationPhase = "waiting-for-click";
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
          resolve();
        } else {
          resolve();
        }
      } else {
        // For intermediate animations, keep it simple
        this.playNextAnimation().then(resolve).catch(reject);
      }
    });
  }

  /**
   * Get animation duration from spine data with detailed extraction
   */
  getSpineAnimationDuration(animationName) {
    if (
      !this.winSpine ||
      !this.winSpine.skeleton ||
      !this.winSpine.skeleton.data ||
      !this.winSpine.skeleton.data.animations
    ) {
      return 1000; // Default fallback
    }

    try {
      // Method 1: Try to get from skeleton data animations array
      const animation = this.winSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName
      );

      if (animation && animation.duration) {
        const durationMs = Math.round(animation.duration * 1000);
        return durationMs;
      }

      // Method 2: Try to get from spine state data
      if (this.winSpine.state && this.winSpine.state.data) {
        const stateAnimation =
          this.winSpine.state.data.skeletonData.animations.find(
            (anim) => anim.name === animationName
          );

        if (stateAnimation && stateAnimation.duration) {
          const durationMs = Math.round(stateAnimation.duration * 1000);
          return durationMs;
        }
      }

      // Method 3: Try to access the raw JSON data if available
      if (this.winSpine.skeleton.data.name && PIXI.Assets.cache) {
        // This might contain the original JSON data
        const skeletonAsset = Object.values(PIXI.Assets.cache._cache).find(
          (asset) =>
            asset && asset.animations && asset.animations[animationName]
        );

        if (skeletonAsset && skeletonAsset.animations[animationName]) {
          // Calculate duration from keyframes if available
          const animData = skeletonAsset.animations[animationName];
          let maxTime = 0;

          // Check all tracks for the longest duration
          Object.values(animData).forEach((track) => {
            if (Array.isArray(track)) {
              track.forEach((keyframe) => {
                if (keyframe.time && keyframe.time > maxTime) {
                  maxTime = keyframe.time;
                }
              });
            }
          });

          if (maxTime > 0) {
            const durationMs = Math.round(maxTime * 1000);
            return durationMs;
          }
        }
      }

      return 1000; // Default fallback
    } catch (error) {
      return 1000; // Default fallback
    }
  }

  /**
   * Play intermediate animation (full cycle then continue to next)
   */
  playIntermediateAnimation(
    inAnimation,
    idleAnimation,
    outAnimation,
    resolve,
    reject
  ) {
    const winType = this.animationQueue[this.currentAnimationIndex];
    const totalDuration = ANIMATION_TIMINGS.SPINE_ANIMATION_DURATION;

    // Calculate counter animation values using simplified ranges
    const { start: counterStartValue, end: counterEndValue } =
      this.getCounterRange(
        winType,
        this.betAmount,
        this.totalWinAmount,
        this.currentAnimationIndex
      );

    if (inAnimation && idleAnimation && outAnimation) {
      // Get actual duration for each phase from spine data
      const inDuration = this.getSpineAnimationDuration(`${winType}_in`);
      const idleDuration = this.getSpineAnimationDuration(`${winType}_idle`);
      const outDuration = this.getSpineAnimationDuration(`${winType}_out`);

      // Start counter animation for this phase (using total duration)
      this.startCounterAnimation(
        counterStartValue,
        counterEndValue,
        totalDuration
      );

      // Step 1: Play in animation for its actual duration
      this.winSpine.state.setAnimation(0, inAnimation, false);

      // Step 2: After in animation completes, start looping idle for its actual duration
      const inTimeout = setTimeout(() => {
        if (this.isPlaying && this.isAppActive) {
          this.winSpine.state.setAnimation(0, idleAnimation, true);

          // Step 3: After idle animation duration, play out animation for its actual duration
          const idleTimeout = setTimeout(() => {
            if (this.isPlaying && this.isAppActive) {
              this.winSpine.state.setAnimation(0, outAnimation, false);

              // Step 4: After out animation completes, move to next
              const outTimeout = setTimeout(() => {
                if (this.isPlaying && this.isAppActive) {
                  this.currentAnimationIndex++;
                  this.phaseStartTime = performance.now();
                  this.phaseProgress = 0;
                  this.playNextAnimation().then(resolve).catch(reject);
                }
              }, outDuration);

              this.currentTimeouts.push(outTimeout);
            }
          }, idleDuration);

          this.currentTimeouts.push(idleTimeout);
        }
      }, inDuration);

      this.currentTimeouts.push(inTimeout);
    } else if (idleAnimation) {
      // Fallback: just idle for specified duration
      this.winSpine.state.setAnimation(0, idleAnimation, true);

      // Start counter animation
      this.startCounterAnimation(
        counterStartValue,
        counterEndValue,
        totalDuration
      );

      const timeout = setTimeout(() => {
        if (this.isPlaying && this.isAppActive) {
          // Move to next animation
          this.currentAnimationIndex++;
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
          this.playNextAnimation().then(resolve).catch(reject);
        }
      }, totalDuration);

      this.currentTimeouts.push(timeout);
    } else {
      // No valid animation, skip to next
      this.currentAnimationIndex++;
      this.playNextAnimation().then(resolve).catch(reject);
    }
  }

  /**
   * Play final animation (in → idle → wait for user)
   */
  playFinalAnimation(inAnimation, idleAnimation, resolve, reject) {
    const winType = this.animationQueue[this.currentAnimationIndex];
    const totalDuration = ANIMATION_TIMINGS.SPINE_ANIMATION_DURATION;

    // Calculate counter animation values using simplified ranges
    const { start: counterStartValue, end: counterEndValue } =
      this.getCounterRange(
        winType,
        this.betAmount,
        this.totalWinAmount,
        this.currentAnimationIndex
      );

    // Get actual duration for in animation from spine data
    const inDuration = this.getSpineAnimationDuration(`${winType}_in`);

    // Start counter animation for final phase (using total duration for the sequence)
    this.startCounterAnimation(
      counterStartValue,
      counterEndValue,
      totalDuration, // Use total duration of in + idle + out
      () => {
        // Counter completed naturally - start auto-close timeout
        this.startAutoCloseTimer();
      }
    );

    if (inAnimation && idleAnimation) {
      // Play in animation for its actual duration
      this.winSpine.state.setAnimation(0, inAnimation, false);

      // After in animation completes, start looping idle animation
      const inTimeout = setTimeout(() => {
        if (this.isPlaying && this.isAppActive) {
          // Add looping idle animation and wait for user click
          this.winSpine.state.addAnimation(0, idleAnimation, true, 0);
          this.isWaitingForClick = true;
          this.currentAnimationPhase = "waiting-for-click";
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
          resolve(); // Animation sequence complete, now waiting for user
        }
      }, inDuration); // Use actual in animation duration

      this.currentTimeouts.push(inTimeout);
    } else if (idleAnimation) {
      // Fallback: just loop idle animation
      this.winSpine.state.addAnimation(0, idleAnimation, true, 0);
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
      resolve();
    } else {
      // No valid final animation
      this.finishAnimation();
      resolve();
    }
  }

  /**
   * Find appropriate animations from available list
   */
  findAnimations(animations, winType) {
    const inAnimation = animations.find((anim) => anim === `${winType}_in`);
    const idleAnimation = animations.find((anim) => anim === `${winType}_idle`);
    const outAnimation = animations.find((anim) => anim === `${winType}_out`);

    return { inAnimation, idleAnimation, outAnimation };
  }

  /**
   * Handle user click to skip counter or close animation
   *
   * Two-click logic:
   * 1. First click (during counter animation): Skips counter to final value
   * 2. Second click (after counter finished): Closes the entire animation
   *
   * Also handles spacebar input and prevents double-clicks with debouncing.
   *
   * @returns {boolean} True if click was handled, false if ignored
   */
  handleUserClick() {
    // Basic validation
    if (!this.isPlaying || !this.winSpine || !this.winSpine.state) {
      return false;
    }

    // Block interactions during closing or finishing phases
    if (this.isClosing || this.isFinishing) {
      return false;
    }

    // Skip protection: block input for first second of animation
    if (!this.canSkipAnimation) {
      return false;
    }

    // Debounce extremely rapid inputs
    if (this.isHandlingUserClick) {
      return false;
    }

    this.isHandlingUserClick = true;

    // Clear existing debounce timeout if any
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = setTimeout(() => {
      this.isHandlingUserClick = false;
      this.debounceTimeout = null;
    }, 300); // Longer debounce to prevent spam

    // Check if counter is currently animating
    const isCounterAnimating = this.isCounterAnimating();
    const currentCounterValue = this.getCurrentCounterValue();
    const isCounterFinished = currentCounterValue >= this.totalWinAmount;

    // SIMPLE LOGIC: If counter is animating, skip it; if finished, close animation
    if (isCounterAnimating && !isCounterFinished) {
      // Counter is still animating - skip to final
      this.hasHandledSkipClick = true;

      // Jump counter to final value immediately
      this.jumpCounterToValue(this.totalWinAmount);

      this.counterJumpedToFinal = true;

      // Skip to final animation
      this.skipToFinalAnimation();

      // Start auto-close timer AFTER skipToFinalAnimation (which clears all timeouts)
      this.startAutoCloseTimer();

      // Clear debounce immediately after processing to allow next click
      this.isHandlingUserClick = false;
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }

      return true;
    } else {
      // Counter is finished or not animating - close animation
      this.hasHandledCloseClick = true;

      // Clear auto-close timer since user clicked manually
      if (this.autoCloseTimeout) {
        clearTimeout(this.autoCloseTimeout);
        this.autoCloseTimeout = null;
      }

      this.playClosingAnimation();

      // Clear debounce immediately after processing
      this.isHandlingUserClick = false;
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }

      return true;
    }
  }

  /**
   * Start auto-close timer for animations
   *
   * Automatically closes win animations if user doesn't interact after counter
   * reaches final value. Prevents animations from staying open indefinitely.
   * Timer is cleared if user clicks manually.
   */
  startAutoCloseTimer() {
    // Clear any existing auto-close timer
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
    }

    // Start auto-close timer using configured delay
    this.autoCloseTimeout = setTimeout(() => {
      if (this.isPlaying && !this.hasHandledCloseClick) {
        this.hasHandledCloseClick = true;
        this.playClosingAnimation();
      }
    }, FEATURES_CONFIG.WIN_CELEBRATION.AUTO_CLOSE_DELAY);
  }

  /**
   * Skip all intermediate animations and jump straight to final animation
   *
   * When user clicks skip during counter animation:
   * - Stops all current animations and timeouts
   * - Jumps counter to final value instantly
   * - Plays final win type animation (EPIC for highest wins)
   * - If already playing final animation, handles smooth transition
   *
   * Used for progressive wins to skip BIG→SUPER→MEGA and go straight to EPIC.
   */
  skipToFinalAnimation() {
    const finalWinType = this.animationQueue[this.animationQueue.length - 1];
    const isAlreadyAtFinalAnimation =
      this.currentAnimationIndex === this.animationQueue.length - 1;

    // If we're already playing the final animation, handle it smoothly
    if (isAlreadyAtFinalAnimation && this.isPlaying) {
      this.handleSkipDuringFinalAnimation(finalWinType);
      return;
    }

    // Clear all timeouts and animations to stop intermediate animations
    this.clearAllResources();

    // Stop current banner sounds only (keep win_music playing - it plays throughout entire sequence)
    this.stopBannerSounds();

    // Jump to final animation index
    this.currentAnimationIndex = this.animationQueue.length - 1;

    // Jump counter to final value immediately when skipping (only if not already jumped)
    if (!this.counterJumpedToFinal) {
      this.jumpCounterToValue(this.totalWinAmount);
      this.counterJumpedToFinal = true; // Mark counter as jumped since we skipped to final
    }

    // Show spine if we're coming from counter-only phase
    if (!this.isVisible) {
      this.winSpine.alpha = 1; // Show (use alpha not visible)
      this.isVisible = true;
    }

    // Play banner sound for the final win type when skipping
    // (main win sound already played at start)
    this.playBannerSound(finalWinType);

    // Setup spine animation for final win type
    this.winSpine.state.clearTracks();
    this.winSpine.state.clearListeners();
    this.winSpine.state.apply(this.winSpine.skeleton);
    this.winSpine.skeleton.setToSetupPose();

    const animations = this.getAvailableAnimations();
    const { inAnimation, idleAnimation } = this.findAnimations(
      animations,
      finalWinType
    );

    // Play final animation sequence (in → idle → wait for user) - skip counter animation since we already jumped
    this.resumeFinalAnimationAfterSkip(
      inAnimation,
      idleAnimation,
      () => {},
      () => {}
    );
  }

  /**
   * Handle skip during final animation
   * Checks current phase and transitions smoothly to idle/out
   */
  handleSkipDuringFinalAnimation(finalWinType) {
    const animations = this.getAvailableAnimations();
    const { idleAnimation } = this.findAnimations(animations, finalWinType);

    // Get current animation being played
    const currentAnimation = this.winSpine.state.tracks[0]?.animation?.name;

    // Check what phase we're currently in
    const inAnimationName = `${finalWinType}_in`;
    const idleAnimationName = `${finalWinType}_idle`;

    if (currentAnimation === inAnimationName) {
      // We're in the IN phase - smoothly transition to IDLE
      this.winSpine.state.clearListeners();

      if (idleAnimation) {
        // Use addAnimation for smooth blend from IN to IDLE
        this.winSpine.state.addAnimation(0, idleAnimation, true, 0);

        // Set waiting state
        this.isWaitingForClick = true;
        this.currentAnimationPhase = "waiting-for-click";
        this.phaseStartTime = performance.now();
        this.phaseProgress = 0;
      }
    } else if (currentAnimation === idleAnimationName) {
      // We're already in IDLE phase - if this was a skip action, stay in idle and wait for user
      // Only close if this wasn't triggered by a skip (i.e., natural progression)
      // Don't close - just ensure we're in waiting state
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
    } else {
      // Fallback: assume we're in IDLE and stay there (don't close)
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
    }
  }

  /**
   * Resume final animation after skip (counter already at final value)
   */
  resumeFinalAnimationAfterSkip(inAnimation, idleAnimation, resolve, reject) {
    // Don't animate counter - it's already at final value from skip

    if (inAnimation && idleAnimation) {
      // Play in animation for exactly 1 second
      this.winSpine.state.setAnimation(0, inAnimation, false);

      // After exactly 1 second, start looping idle animation
      const inTimeout = setTimeout(() => {
        if (this.isPlaying && this.isAppActive) {
          // Add looping idle animation and wait for user click
          this.winSpine.state.addAnimation(0, idleAnimation, true, 0);
          this.isWaitingForClick = true;
          this.currentAnimationPhase = "waiting-for-click";
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
          resolve(); // Animation sequence complete, now waiting for user
        }
      }, 1000); // 1 second

      this.currentTimeouts.push(inTimeout);
    } else if (idleAnimation) {
      // Fallback: just loop idle animation
      this.winSpine.state.addAnimation(0, idleAnimation, true, 0);
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
      resolve();
    } else {
      // No valid final animation
      this.finishAnimation();
      resolve();
    }
  }

  /**
   * Play closing out animation when user clicks to continue
   */
  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.winSpine?.state) return;

    this.isClosing = true;
    this.isWaitingForClick = false;
    this.hideClickIndicator();
    this.hideButton();

    // Stop win_music and play win_music_end when closing (no fadeout, just stop and play)
    try {
      soundManager.stop(SOUND_NAME.win_music);
      soundManager.play(SOUND_NAME.win_music_end);
    } catch (error) {
      console.log("Failed to stop win_music or play win_music_end:", error);
    }

    // DON'T hide counter here - let it follow the spine's OUT animation
    // Counter will be hidden in finishAnimation() after spine completes

    const animations = this.getAvailableAnimations();
    const { outAnimation } = this.findAnimations(animations, this.finalWinType);
    const outAnim = outAnimation || animations[0];

    this.winSpine.state.clearListeners();

    if (outAnim) {
      this.winSpine.state.setAnimation(0, outAnim, false);
      this.winSpine.state.addListener({
        complete: (e) => {
          if (e.animation.name === outAnim && this.isPlaying) {
            this.finishAnimation();
          }
        },
      });
    } else {
      this.finishAnimation();
    }
  }

  /**
   * Complete the animation sequence and perform full cleanup
   *
   * Called when user closes animation or auto-close timer expires.
   * Performs final cleanup: stops sounds, hides UI, clears resources,
   * dispatches Redux state changes, and resets all animation state.
   * This is the final method called when win celebration ends.
   */
  finishAnimation() {
    if (!this.winSpine || !this.initialized) {
      return;
    }

    // Set finishing flag immediately to prevent retriggering
    this.isFinishing = true;

    this.stopBoneUpdateLoop();

    // Dispatch Redux state change immediately before any async operations
    if (this.dispatch) {
      this.dispatch(setWinAnimationsPlaying(false));
      // Unblock symbol popup when win animations finish
      this.dispatch(setSymbolPopupBlocked(false));
    }

    // Stop win_music when animation finishes (should already be stopped in playClosingAnimation, but safety stop)
    // Use stop (not fadeOut) since win_music_end already played during closing animation
    try {
      if (SOUND_NAME.win_music) {
        soundManager.stop(SOUND_NAME.win_music);
      }
    } catch (error) {
      // Silent fail
    }

    // Resume background music if it was playing before
    this.resumeBackgroundMusic();

    // Clear all resources to prevent buildup
    this.clearAllResources();

    // Reset playing states
    this.isPlaying = false;
    this.isWaitingForClick = false;

    // Disable scaling calculations when animation finishes
    this.shouldBeActive = false;

    // Reset activity state
    this.isAppActive = true;
    this.pausedTimeouts = [];
    this.totalPausedDuration = 0;
    this.currentAnimationPhase = null;
    this.phaseStartTime = 0;
    this.phaseProgress = 0;

    // Counter doesn't need manual hiding - it's a child of spine and will hide with it
    // The spine's alpha change will automatically hide the counter

    // Hide click indicator instantly (cleanup)
    this.hideClickIndicatorInstant();

    // Hide spine immediately when finishAnimation starts to prevent visibility issues
    if (this.winSpine) {
      this.winSpine.alpha = 0.001; // Hide (use alpha not visible)
    }
    this.isVisible = false;

    // Fade out background with callback to clean up state after animation completes
    this.overlayRef.current?.fadeOutBackground(() => {
      // Stop banner sounds when overlay fades out completely
      this.stopBannerSounds();

      // Double-check spine is still hidden (defensive programming)
      if (this.winSpine) {
        this.winSpine.alpha = 0.001; // Hide (use alpha not visible)
      }
      this.isVisible = false;
    });

    // Reset animation queue and stored values
    this.animationQueue = [];
    this.currentAnimationIndex = 0;
    this.finalWinType = null;
    this.totalWinAmount = null;
    this.betAmount = null;
    this.lastWinAmount = null; // Reset to allow new win animations
    this.counterJumpedToFinal = false;

    // Reset user interaction guards
    this.hasHandledSkipClick = false;
    this.hasHandledCloseClick = false;
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;
    this.isClosing = false; // Reset closing state

    // Reset spine timeScale in case it was paused
    if (this.winSpine && this.winSpine.state) {
      this.winSpine.state.timeScale = 1;
    }

    // Reset auto-close timeout
    this.autoCloseTimeout = null;

    // Reset finishing flag after all cleanup is complete
    this.isFinishing = false;
  }

  /**
   * Immediately stop all win animations and perform emergency cleanup
   *
   * Used when component unmounts or critical errors occur.
   * Unlike finishAnimation(), this doesn't dispatch Redux state changes
   * or perform full UI transitions - just stops everything immediately.
   */
  stopWinAnimation() {
    this.stopBoneUpdateLoop();

    // Clear all resources (timeouts + animations)
    this.clearAllResources();

    // Stop all win sounds
    this.stopAllWinSounds();

    // Resume background music if it was playing before
    this.resumeBackgroundMusic();

    if (this.winSpine && this.isPlaying) {
      this.winSpine.state?.clearListeners();
      this.winSpine.state?.clearTracks();
      this.winSpine.alpha = 0.001; // Hide (use alpha not visible)
      this.isPlaying = false;
      this.isVisible = false;

      // Disable scaling calculations when animation is stopped
      this.shouldBeActive = false;

      // Hide click indicator instantly (cleanup)
      this.hideClickIndicatorInstant();

      this.overlayRef.current?.fadeOutBackground();
    }

    // Reset user interaction guards
    this.hasHandledSkipClick = false;
    this.hasHandledCloseClick = false;
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;
    this.isClosing = false; // Reset closing state

    // Reset activity state
    this.isAppActive = true;
    this.pausedTimeouts = [];
    this.totalPausedDuration = 0;
    this.currentAnimationPhase = null;
    this.phaseStartTime = 0;
    this.phaseProgress = 0;

    // Reset finishing flag if animation was stopped
    this.isFinishing = false;
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.stopBoneUpdateLoop();

    // Clear all resources before cleanup
    this.clearAllResources();

    // Cleanup counter
    if (this.counterContainer) {
      this.counterBitmapTexts.forEach((text) => text.destroy());
      this.counterBitmapTexts = [];
      this.counterContainer.destroy({ children: true });
      this.counterContainer = null;
    }

    // Cleanup button
    if (this.buttonContainer) {
      if (this.buttonText) {
        this.buttonText.destroy();
        this.buttonText = null;
      }
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
    }

    // Disable scaling calculations when animation is no longer active
    this.shouldBeActive = false;

    if (this.winSpine) {
      // Defensive cleanup - ensure spine state is properly cleared
      if (this.winSpine.state) {
        try {
          this.winSpine.state.clearListeners();
          this.winSpine.state.clearTracks();
          this.winSpine.state.timeScale = 1.0;
        } catch (error) {
          // Spine might already be destroyed - continue cleanup
        }
      }

      // Remove from container safely
      if (this.container && this.container.children.includes(this.winSpine)) {
        this.container.removeChild(this.winSpine);
      }

      // Destroy spine instance
      try {
        this.winSpine.destroy();
      } catch (error) {
        // Spine might already be destroyed - continue cleanup
      }

      this.winSpine = null;
    }

    this.initialized = false;
  }
}

export default WinAnimationController;
